import React, { useEffect, useState } from 'react';
import AppLayout from '../components/AppLayout';
import { db } from '../firebase';
import { collection, addDoc, getDocs, updateDoc, doc, deleteDoc, query, where } from 'firebase/firestore';
import { ROLES, useUserRole, hasRole } from '../lib/auth/roles';
import { getStaffDisplayName, getStaffRoleLabel } from '../lib/staff';
import StaffRoleSelect from '../components/StaffRoleSelect';
import { useSchoolId } from '../lib/auth/schoolContext';
import { staffAvatar } from '../lib/avatar';
import { Icons } from '../lib/Icons';

export default function StaffDirectory() {
  const { role } = useUserRole();
  const canEdit = hasRole(role, [ROLES.SENCO]);
  const { schoolId } = useSchoolId();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', role: ROLES.TEACHER, hourlyRate: '', dayRate: '', active: true });
  const [editingId, setEditingId] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [viewStaff, setViewStaff] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const staffQuery = query(collection(db, 'staff'), where('schoolId', '==', schoolId));
      const snap = await getDocs(staffQuery);
      const staffData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort by name on the client side
      staffData.sort((a, b) => getStaffDisplayName(a).localeCompare(getStaffDisplayName(b)));
      setStaff(staffData);
    } catch (error) {
      console.error('Error fetching staff:', error);
      // If there's a permission error or any other error, just show empty staff list
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (schoolId) fetchStaff(); }, [schoolId]);

  const resetForm = () => setForm({ name: '', email: '', role: ROLES.TEACHER, hourlyRate: '', dayRate: '', active: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        hourlyRate: Number(form.hourlyRate) || 0,
        dayRate: Number(form.dayRate) || 0,
        active: !!form.active,
        updatedAt: new Date(),
      };
      if (editingId) {
        await updateDoc(doc(db, 'staff', editingId), payload);
        setEditingId(null);
        setIsEditModalOpen(false);
      } else {
        payload.createdAt = new Date();
        await addDoc(collection(db, 'staff'), {
          ...payload,
          schoolId,
          avatarUseGenerated: true,
        });
      }
      resetForm();
      fetchStaff();
    } catch (error) {
      console.error('Error saving staff member:', error);
      alert('Failed to save staff member. Please try again.');
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: getStaffDisplayName(s),
      email: s.email || '',
      role: s.role || ROLES.TEACHER,
      hourlyRate: s.hourlyRate ?? '',
      dayRate: s.dayRate ?? '',
      active: !!s.active,
    });
    setIsEditModalOpen(true);
  };

  const openView = (s) => {
    setViewStaff(s);
    setIsViewModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!canEdit) return;
    if (!window.confirm('Delete this staff member?')) return;
    
    try {
      await deleteDoc(doc(db, 'staff', id));
      fetchStaff();
    } catch (error) {
      console.error('Error deleting staff member:', error);
      alert('Failed to delete staff member. Please try again.');
    }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-xl">
              <Icons.Users className="h-7 w-7 text-blue-600" />
            </div>
            Staff Directory
          </h1>
          <p className="text-gray-600 mt-2">Manage staff and rates for costing interventions.</p>
        </div>

        {canEdit && (
          <div className="bg-white shadow rounded p-6">
            <h2 className="text-lg font-semibold mb-4">{editingId ? 'Edit Staff' : 'Add Staff'}</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <input className="md:col-span-2 border rounded p-2" placeholder="Name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} required />
              <input className="md:col-span-2 border rounded p-2" placeholder="Email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} required />
              <StaffRoleSelect
                className="md:col-span-1 border rounded p-2"
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              />
              <label className="md:col-span-1 inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={e=>setForm(f=>({...f,active:e.target.checked}))} /> Active
              </label>
              <input className="md:col-span-2 border rounded p-2" type="number" step="0.01" placeholder="Hourly Rate (£)" value={form.hourlyRate} onChange={e=>setForm(f=>({...f,hourlyRate:e.target.value}))} />
              <input className="md:col-span-2 border rounded p-2" type="number" step="0.01" placeholder="Day Rate (£)" value={form.dayRate} onChange={e=>setForm(f=>({...f,dayRate:e.target.value}))} />
              <div className="md:col-span-2 flex gap-2 items-center">
                <button className="px-4 py-2 bg-indigo-600 text-white rounded">{editingId ? 'Save' : 'Add'}</button>
                {editingId && <button type="button" onClick={()=>{setEditingId(null);resetForm();}} className="px-4 py-2 border rounded">Cancel</button>}
              </div>
            </form>
          </div>
        )}

        {/* Card grid UX */}
        <div className="bg-white shadow rounded">
          <div className="p-4 border-b">
            <h2 className="font-semibold">Our Staff</h2>
            <p className="text-sm text-gray-500">Click a card to view full details</p>
          </div>
          {loading ? (
            <div className="p-6 text-gray-500">Loading…</div>
          ) : staff.length === 0 ? (
            <div className="p-6 text-gray-500">No staff yet</div>
          ) : (
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {staff.map((s) => (
                <button
                  key={s.id}
                  onClick={() => openView(s)}
                  className="text-left rounded-2xl bg-gray-50 px-6 py-6 hover:bg-gray-100 transition dark:bg-gray-800 dark:hover:bg-gray-700"
                >
                  <img
                    alt=""
                    src={staffAvatar(s, 160)}
                    className="mx-auto h-28 w-28 rounded-full outline-1 -outline-offset-1 outline-black/5 object-cover"
                  />
                  <h3 className="mt-4 text-base font-semibold tracking-tight text-gray-900">{getStaffDisplayName(s)}</h3>
                  <p className="text-sm text-gray-600">{getStaffRoleLabel(s.role)}{s.active === false ? ' • inactive' : ''}</p>
                  <div className="mt-3 text-xs text-gray-500">{s.email}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Removed legacy list to streamline UX */}
      </div>
      {/* View Staff Modal */}
      {isViewModalOpen && viewStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setIsViewModalOpen(false); setViewStaff(null); }} />
          <div className="relative bg-white rounded shadow-lg w-full max-w-xl mx-4">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Staff Details</h3>
              <button onClick={() => { setIsViewModalOpen(false); setViewStaff(null); }} className="px-2 py-1 text-gray-600">✕</button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1 flex flex-col items-center">
                <img
                  alt=""
                  src={staffAvatar(viewStaff, 160)}
                  className="h-28 w-28 rounded-full outline-1 -outline-offset-1 outline-black/5 object-cover"
                />
                <div className="mt-3 text-center">
                  <div className="font-medium">{getStaffDisplayName(viewStaff)}</div>
                  <div className="text-sm text-gray-600">{getStaffRoleLabel(viewStaff.role)}</div>
                  {viewStaff.active === false && <div className="text-xs text-gray-500">Inactive</div>}
                </div>
              </div>
              <div className="md:col-span-2 space-y-3">
                <div>
                  <div className="text-xs text-gray-500">Email</div>
                  <div className="text-sm text-gray-800">{viewStaff.email || '-'}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-gray-500">Hourly Rate</div>
                    <div className="text-sm text-gray-800">£{Number(viewStaff.hourlyRate || 0).toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Day Rate</div>
                    <div className="text-sm text-gray-800">£{Number(viewStaff.dayRate || 0).toFixed(2)}</div>
                  </div>
                </div>
                <div className="pt-2 border-t">
                  <div className="text-xs text-gray-500">Actions</div>
                  <div className="mt-2 flex gap-2">
                    {canEdit && (
                      <button
                        onClick={() => { setIsViewModalOpen(false); startEdit(viewStaff); }}
                        className="px-3 py-1.5 border rounded"
                      >
                        Edit
                      </button>
                    )}
                    <a href={`mailto:${viewStaff.email || ''}`} className="px-3 py-1.5 border rounded">Email</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {isEditModalOpen && canEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setIsEditModalOpen(false); setEditingId(null); resetForm(); }} />
          <div className="relative bg-white rounded shadow-lg w-full max-w-2xl mx-4">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Edit Staff Member</h3>
              <button onClick={() => { setIsEditModalOpen(false); setEditingId(null); resetForm(); }} className="px-2 py-1 text-gray-600">✕</button>
            </div>
            <div className="p-4">
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <input className="md:col-span-2 border rounded p-2" placeholder="Name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} required />
                <input className="md:col-span-2 border rounded p-2" placeholder="Email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} required />
                <StaffRoleSelect
                  className="md:col-span-2 border rounded p-2"
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                />
                <input className="md:col-span-3 border rounded p-2" type="number" step="0.01" placeholder="Hourly Rate (£)" value={form.hourlyRate} onChange={e=>setForm(f=>({...f,hourlyRate:e.target.value}))} />
                <input className="md:col-span-3 border rounded p-2" type="number" step="0.01" placeholder="Day Rate (£)" value={form.dayRate} onChange={e=>setForm(f=>({...f,dayRate:e.target.value}))} />
                <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.active} onChange={e=>setForm(f=>({...f,active:e.target.checked}))} /> Active
                </label>
                <div className="md:col-span-4 flex gap-2 items-center justify-end">
                  <button type="button" onClick={()=>{ setIsEditModalOpen(false); setEditingId(null); resetForm(); }} className="px-4 py-2 border rounded">Cancel</button>
                  <button className="px-4 py-2 bg-indigo-600 text-white rounded">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
